import qs from 'qs';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
    PickListFilterShippedStatus,
    type MarkAsPaidBySellerApiRequest,
    type MarkAsPaidBySellerApiResponse,
    type MemberPickListV2ApiResponse,
    type PicklistV2Request,
    type SetItemShippedApiRequest,
    type SetItemShippedApiResponse,
    type SetTransactionVisibilityApiRequest,
    type SetTransactionVisibilityApiResponse,
    PickListFilterPaidStatus,
} from 'tradera/state/services/types/webapi-member-generated';
import { isomorphicWebApiBaseQuery } from 'tradera/state/services/utils/webapi-base-query';
import {
    updatePaidStatusForBuyerItems,
    updateShippingStatusForBuyerItems,
    updateVisibilityStatusForBuyerItems,
} from './pick-list-item-updater';
import type { AppDispatch } from 'tradera/state/configure-store';

export const PickListTagTypes = { PickListItems: 'PickListItems' } as const;
const getPickListItemsCacheKeys: Set<PicklistV2Request> = new Set();

export const pickListApi = createApi({
    reducerPath: 'pickListApi',
    baseQuery: isomorphicWebApiBaseQuery,
    tagTypes: Object.values(PickListTagTypes),
    endpoints: (builder) => ({
        markAsShipped: builder.mutation<
            SetItemShippedApiResponse,
            SetItemShippedApiRequest
        >({
            query: (payload) => {
                return {
                    url: '/api/webapi/member/selling/item-shipped',
                    method: 'POST',
                    body: payload,
                };
            },
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                const patchResults = [];
                for (const cacheKey of getPickListItemsCacheKeys) {
                    const patchResult = dispatch(
                        pickListApi.util.updateQueryData(
                            'getPickListItems',
                            cacheKey,
                            (draft) => {
                                const updatedItems =
                                    updateShippingStatusForBuyerItems(
                                        patch,
                                        draft.items,
                                    );

                                Object.assign(draft, {
                                    items: updatedItems,
                                });
                            },
                        ),
                    );

                    patchResults.push(patchResult);
                }

                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            },
        }),
        markAsPaidBySeller: builder.mutation<
            MarkAsPaidBySellerApiResponse,
            MarkAsPaidBySellerApiRequest
        >({
            query: (payload) => {
                return {
                    url: '/api/webapi/member/selling/marked-as-paid-by-seller',
                    method: 'POST',
                    body: payload,
                };
            },
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                const patchResults = [];
                for (const cacheKey of getPickListItemsCacheKeys) {
                    const patchResult = dispatch(
                        pickListApi.util.updateQueryData(
                            'getPickListItems',
                            cacheKey,
                            (draft) => {
                                const updatedItems =
                                    updatePaidStatusForBuyerItems(
                                        patch,
                                        draft.items,
                                    );

                                Object.assign(draft, {
                                    items: updatedItems,
                                });
                            },
                        ),
                    );

                    patchResults.push(patchResult);
                }

                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            },
        }),
        setItemVisibility: builder.mutation<
            SetTransactionVisibilityApiResponse,
            SetTransactionVisibilityApiRequest
        >({
            query: (payload) => {
                return {
                    url: '/api/webapi/member/transaction-visibility',
                    method: 'POST',
                    body: payload,
                };
            },
            transformResponse: (res: SetTransactionVisibilityApiResponse) => {
                if (res?.responseStatus?.errorCode) {
                    throw new Error('Error occured');
                }

                return res;
            },
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                /*
                 * On each request to getPickListItems we store the request in a list.
                 * The request is used as a key to the cache for a request. By storing all the
                 * requests it allows us to go through the cache and update the items and
                 * perform optimistic updates.
                 *
                 * So what we do here is go through the cache of all the requests that the user has
                 * done to getPickListItems
                 */
                const patchResults = [];
                for (const cacheKey of getPickListItemsCacheKeys) {
                    /*
                     * Here we are accessing the cache for getPickListItems and performing an update
                     * on the items, simulating what the backend would do to them. draft is the items
                     * and patch is the mutation arguments, in this case: purchaseOrderId and hidden.
                     *
                     * updateVisibilityStatusForBuyerItems will go through all the buyers and set
                     * isHiddenBySeller to true or false depending on what the patch.hidden is,
                     * then filter away all the items that is no longer matching the patch
                     */
                    const patchResult = dispatch(
                        pickListApi.util.updateQueryData(
                            'getPickListItems',
                            cacheKey,
                            (draft) => {
                                const updatedBuyers =
                                    updateVisibilityStatusForBuyerItems(
                                        patch,
                                        draft.items,
                                    );

                                Object.assign(draft, {
                                    items: updatedBuyers,
                                });
                            },
                        ),
                    );

                    /*
                     * We add every patch to a list of patches. If the request fails
                     * then we will go through every patch and undo it.
                     */
                    patchResults.push(patchResult);
                }

                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            },
            invalidatesTags: (resp, err, req) => {
                const cacheKeys = [...getPickListItemsCacheKeys];

                /*
                 * If we have more than one page, then we want to invalidate all tags
                 * since the items will shift around
                 */
                const hasMoreThanOnePage = cacheKeys.some(
                    (key) => key?.skip ?? 0 >= (key?.take ?? -1),
                );

                if (hasMoreThanOnePage) {
                    return [
                        {
                            type: PickListTagTypes.PickListItems,
                        },
                    ];
                }

                /*
                 * If the request goes well, then we do want to invalidate some tags.
                 * By invalidating the tags we are telling RTKQ that the cache of these requests is
                 * no longer valid and we want to refetch that request the next time it is requested.
                 * We decide which tags to be invalidated by going through all cacheKeys, which
                 * conveniently has the same id as the ids to the tags.
                 *
                 * Since we can easily remove items from the cache, it is not as easy to add items.
                 * Therefore we will invalidate those tags where an item has been added. When we
                 * hide an item, that means that we added an item to the request that has the filter
                 * retrieveHidden = false. So we can invalidate all the tags where this item would now
                 * appear (key.retrieveHidden === req.hidden)
                 */
                const tagsToInvalidate = cacheKeys
                    .filter((key) => key.retrieveHidden === req.hidden)
                    .map((key) => ({
                        type: PickListTagTypes.PickListItems,
                        id: JSON.stringify(key),
                    }));

                return tagsToInvalidate;
            },
        }),
        getPickListItems: builder.query<
            MemberPickListV2ApiResponse,
            PicklistV2Request
        >({
            query: (query) => {
                getPickListItemsCacheKeys.add(query);

                const stringified = qs.stringify(query);
                return `/api/webapi/member/selling/picklist-v2?${stringified}`;
            },

            providesTags: (resp, err, req) => [
                {
                    type: PickListTagTypes.PickListItems,
                    id: JSON.stringify(req),
                },
            ],
        }),
    }),
});

export const invalidateTagsOnShippedStatus = () => (dispatch: AppDispatch) => {
    const cacheKeys = [...getPickListItemsCacheKeys];
    const tagsToInvalidate = cacheKeys
        .filter((key) => key.shippedStatus !== PickListFilterShippedStatus.All)
        .map((key) => ({
            type: PickListTagTypes.PickListItems,
            id: JSON.stringify(key),
        }));

    dispatch(pickListApi.util.invalidateTags(tagsToInvalidate));
};

export const invalidateTagsOnPaidStatus = () => (dispatch: AppDispatch) => {
    const cacheKeys = [...getPickListItemsCacheKeys];
    const tagsToInvalidate = cacheKeys
        .filter((key) => key.paidStatus !== PickListFilterPaidStatus.All)
        .map((key) => ({
            type: PickListTagTypes.PickListItems,
            id: JSON.stringify(key),
        }));

    dispatch(pickListApi.util.invalidateTags(tagsToInvalidate));
};

export const {
    useMarkAsShippedMutation,
    useMarkAsPaidBySellerMutation,
    useSetItemVisibilityMutation,
    useGetPickListItemsQuery,
} = pickListApi;
